/**
 * 能力等级变化量
 */
export class DeltaAbilityLevel {
    /**
     * 攻击
     */
    pAtk?: number = 0;
    /**
     * 特攻
     */
    mAtk?: number = 0;
    /**
     * 防御
     */
    pDef?: number = 0;
    /**
     * 特防
     */
    mDef?: number = 0;
    /**
     * 速度
     */
    speed?: number = 0;
    /**
     * 命中
     */
    hit?: number = 0;
}

/**
 * AbilityLevel：封装本模块的领域数据与相关操作。
 */
export class AbilityLevel {
    /**
     * 攻击
     */
    pAtk: number = 0;
    /**
     * 特攻
     */
    mAtk: number = 0;
    /**
     * 防御
     */
    pDef: number = 0;
    /**
     * 特防
     */
    mDef: number = 0;
    /**
     * 速度
     */
    speed: number = 0;
    /**
     * 命中
     */
    hit: number = 0;

    /**
     * 改变能力等级，取值范围 [-6, 6]
     * @param delta 能力等级变化量
     */
    change(delta: DeltaAbilityLevel) {
        this.pAtk += delta.pAtk ? delta.pAtk : 0;
        this.mAtk += delta.mAtk ? delta.mAtk : 0;
        this.pDef += delta.pDef ? delta.pDef : 0;
        this.mDef += delta.mDef ? delta.mDef : 0;
        this.speed += delta.speed ? delta.speed : 0;
        this.hit += delta.hit ? delta.hit : 0;
        this.handleOverflow();
    }

    /**
     * 处理溢出
     */
    handleOverflow() {
        this.pAtk = this.pAtk > 6 ? 6 : this.pAtk;
        this.mAtk = this.mAtk > 6 ? 6 : this.mAtk;
        this.pDef = this.pDef > 6 ? 6 : this.pDef;
        this.mDef = this.mDef > 6 ? 6 : this.mDef;
        this.speed = this.speed > 6 ? 6 : this.speed;
        this.hit = this.hit > 6 ? 6 : this.hit;

        this.pAtk = this.pAtk < -6 ? -6 : this.pAtk;
        this.mAtk = this.mAtk < -6 ? -6 : this.mAtk;
        this.pDef = this.pDef < -6 ? -6 : this.pDef;
        this.mDef = this.mDef < -6 ? -6 : this.mDef;
        this.speed = this.speed < -6 ? -6 : this.speed;
        this.hit = this.hit < -6 ? -6 : this.hit;
    }

    /**
     * 重置能力等级
     */
    reset() {
        this.pAtk = 0;
        this.mAtk = 0;
        this.pDef = 0;
        this.mDef = 0;
        this.speed = 0;
        this.hit = 0;
    }

    /**
     * 消除强化
     * @returns 是否成功
     */
    clearLevelUp() {
        // TODO: 强化无法消除

        this.pAtk = this.pAtk > 0 ? 0 : this.pAtk;
        this.mAtk = this.mAtk > 0 ? 0 : this.mAtk;
        this.pDef = this.pDef > 0 ? 0 : this.pDef;
        this.mDef = this.mDef > 0 ? 0 : this.mDef;
        this.speed = this.speed > 0 ? 0 : this.speed;
        this.hit = this.hit > 0 ? 0 : this.hit;

        return true;
    }

    /**
     * 解除弱化
     * @returns 是否成功
     */
    clearLevelDown() {
        this.pAtk = this.pAtk < 0 ? 0 : this.pAtk;
        this.mAtk = this.mAtk < 0 ? 0 : this.mAtk;
        this.pDef = this.pDef < 0 ? 0 : this.pDef;
        this.mDef = this.mDef < 0 ? 0 : this.mDef;
        this.speed = this.speed < 0 ? 0 : this.speed;
        this.hit = this.hit < 0 ? 0 : this.hit;

        return true;
    }

    /**
     * 全属性能力提升
     * @param level 等级，默认全属性+1
     */
    allLevelUp(level: number = 1) {
        this.change({
            pAtk: level,
            mAtk: level,
            pDef: level,
            mDef: level,
            speed: level,
            hit: level
        });
    }

    /**
     * 全属性能力降低
     * @param level 等级，默认全属性-1
     */
    allLevelDown(level: number = 1) {
        this.change({
            pAtk: -level,
            mAtk: -level,
            pDef: -level,
            mDef: -level,
            speed: -level,
            hit: -level
        });
    }

    /**
     * 
     * @returns 是否处于强化状态
     */
    isLevelUp() {
        return this.pAtk > 0 ||
            this.mAtk > 0 ||
            this.pDef > 0 ||
            this.mDef > 0 ||
            this.speed > 0 ||
            this.hit > 0;
    }

    /**
     * 
     * @returns 是否处于弱化状态
     */
    isLevelDown() {
        return this.pAtk < 0 ||
            this.mAtk < 0 ||
            this.pDef < 0 ||
            this.mDef < 0 ||
            this.speed < 0 ||
            this.hit < 0;
    }
}

/**
 * Ability 的数据结构或可选取值约定。
 */
export enum Ability {
    /**
     * pAtk，保存当前实例的pAtk状态。
     */
    pAtk,
    /**
     * mAtk，保存当前实例的mAtk状态。
     */
    mAtk,
    /**
     * pDef，保存当前实例的pDef状态。
     */
    pDef,
    /**
     * mDef，保存当前实例的mDef状态。
     */
    mDef,
    /**
     * speed，保存当前实例的speed状态。
     */
    speed,
    /**
     * curHP，保存当前实例的curHP状态。
     */
    curHP,
    /**
     * maxHP，保存当前实例的maxHP状态。
     */
    maxHP,
}